import * as React from "react";
import { Loader } from "lucide-react";

import { Button } from "../ui/button";

import { useDeleteRequest } from "@/hooks/useDeleteRequest";
import { useRequestGallery } from "./RequestGalleryContext";
import { Request } from "@/types/research.types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import RequestItem from "./RequestItem";

interface RequestListProps {
  onRequestSelect: (content: string, request?: Request) => void;
}

const RequestList: React.FC<RequestListProps> = ({ onRequestSelect }) => {
  const {
    searchQuery,
    activeCategoryId,
    setEditingRequest,
    setMode,
    requests,
    isLoadingRequests,
  } = useRequestGallery();

  const deleteRequestMutation = useDeleteRequest();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [deletingRequestId, setDeletingRequestId] = React.useState<string | null>(null);

  const filteredRequests = React.useMemo(() => {
    if (!requests || !activeCategoryId) return [];
    let filtered = requests.filter((req) =>
      activeCategoryId === "all" ? !req.category_id : req.category_id === activeCategoryId
    );

    if (searchQuery.trim()) {
      filtered = filtered.filter(
        (req) =>
          req.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          req.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return [...filtered].reverse();
  }, [requests, activeCategoryId, searchQuery]);

  const handleRequestClick = (content: string, request: Request) => {
    onRequestSelect(content, request);
  };

  const handleDeleteRequest = (e: React.MouseEvent, requestId: string) => {
    e.stopPropagation();
    setDeletingRequestId(requestId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteRequest = () => {
    if (deletingRequestId) {
      deleteRequestMutation.mutate(deletingRequestId);
      setIsDeleteDialogOpen(false);
      setDeletingRequestId(null);
    }
  };

  const handleEditClick = (e: React.MouseEvent, request: Request) => {
    e.stopPropagation();
    setEditingRequest(request);
    setMode("edit-request");
  };

  return (
    <>
      {isLoadingRequests ? (
        <div className="flex flex-col items-center justify-center h-full p-6">
          <Loader className="animate-spin" />
          <span className="ml-2">Loading requests...</span>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center text-neutral-500 text-sm">
          <p>{searchQuery ? "No requests found" : "No requests in this category"}</p>
        </div>
      ) : (
        filteredRequests.map((request) => (
          <RequestItem
            key={request.id}
            request={request}
            onClick={handleRequestClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteRequest}
          />
        ))
      )}


      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="p-4">
          <DialogHeader className="sm:text-center">
            <DialogTitle className="text-base font-medium text-center">Delete Request</DialogTitle>
            <DialogDescription className="text-center text-sm">
              Are you sure you want to delete this request? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2 sm:justify-center">
            <Button
              variant="secondary"
              onClick={() => setIsDeleteDialogOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteRequest} className="flex-1">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RequestList;
